import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApiService, useApiRoutes, useApiPlugins } from './queries';
import { hasOps } from '../../lib/auth';

// Components

function ServiceInfo({ service }: { service: any }) {
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">
        {t('apis.serviceDetail.serviceInfo', 'Service Information')}
      </h2>
      <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
        <div>
          <dt className="text-sm font-medium text-gray-500">
            {t('apis.serviceDetail.name', 'Name')}
          </dt>
          <dd className="mt-1 text-sm text-gray-900">{service.name}</dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-500">
            {t('apis.serviceDetail.upstream', 'Upstream')}
          </dt>
          <dd className="mt-1 text-sm text-gray-900">
            {service.protocol}://{service.host}:{service.port}{service.path}
          </dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-500">
            {t('apis.serviceDetail.status', 'Status')}
          </dt>
          <dd className="mt-1">
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                service.healthy
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {service.healthy ? '● Healthy' : '● Down'}
            </span>
          </dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-500">
            {t('apis.serviceDetail.tags', 'Tags')}
          </dt>
          <dd className="mt-1 text-sm text-gray-900">
            <div className="flex flex-wrap gap-1">
              {service.tags.map((tag: string) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-2 py-1 rounded bg-blue-100 text-blue-800 text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>
          </dd>
        </div>
      </dl>
      
      {service.metrics && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="text-sm font-medium text-gray-900 mb-3">
            {t('apis.serviceDetail.metrics', 'Metrics')}
          </h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-semibold text-gray-900">
                {service.metrics.rqps.toFixed(1)}
              </div>
              <div className="text-sm text-gray-500">rq/s</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-gray-900">
                {(service.metrics.p95 * 1000).toFixed(0)}ms
              </div>
              <div className="text-sm text-gray-500">p95 latency</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-gray-900">
                {service.metrics['5xx']}
              </div>
              <div className="text-sm text-gray-500">5xx errors</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RoutesList({ routes }: { routes: any[] }) {
  const { t } = useTranslation();
  const canManage = hasOps();

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">
            {t('apis.serviceDetail.routes', 'Routes')}
          </h2>
          {canManage && (
            <button className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
              {t('apis.serviceDetail.addRoute', 'Add Route')}
            </button>
          )}
        </div>
      </div>
      
      {routes.length === 0 ? (
        <div className="p-6 text-center">
          <div className="text-gray-400 text-4xl mb-3">🛣️</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('apis.serviceDetail.noRoutes', 'No routes configured')}
          </h3>
          <p className="text-gray-600">
            {t('apis.serviceDetail.noRoutesDescription', 'Add routes to expose this service through Kong.')}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('apis.serviceDetail.path', 'Path')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('apis.serviceDetail.methods', 'Methods')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('apis.serviceDetail.auth', 'Auth')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('apis.serviceDetail.limits', 'Limits')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('apis.serviceDetail.actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {routes.map((route) => (
                <tr key={route.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {route.paths.join(', ')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {route.methods.join(', ')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      route.auth === 'jwt' ? 'bg-blue-100 text-blue-800' :
                      route.auth === 'key' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {route.auth.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {route.limits ? (
                      <div>
                        <div>{route.limits.minute}/min</div>
                        <div className="text-gray-500">{route.limits.sizeMB}MB</div>
                      </div>
                    ) : (
                      <span className="text-gray-400">None</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <Link
                      to={`/apis/route/${route.id}`}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      {t('apis.serviceDetail.viewRoute', 'View')}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PluginsList({ plugins }: { plugins: any[] }) {
  const { t } = useTranslation();
  const canManage = hasOps();

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">
            {t('apis.serviceDetail.plugins', 'Plugins')}
          </h2>
          {canManage && (
            <button className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500">
              {t('apis.serviceDetail.attachPlugin', 'Attach Plugin')}
            </button>
          )}
        </div>
      </div>
      
      {plugins.length === 0 ? (
        <div className="p-6 text-center">
          <div className="text-gray-400 text-4xl mb-3">🔌</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('apis.serviceDetail.noPlugins', 'No plugins attached')}
          </h3>
          <p className="text-gray-600">
            {t('apis.serviceDetail.noPluginsDescription', 'Attach plugins to add functionality like rate limiting, authentication, etc.')}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-gray-200">
          {plugins.map((plugin) => (
            <div key={plugin.id} className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {plugin.name}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {t('apis.serviceDetail.pluginId', 'Plugin ID')}: {plugin.id}
                  </p>
                </div>
                {canManage && (
                  <div className="flex space-x-2">
                    <button className="text-blue-600 hover:text-blue-900 text-sm">
                      {t('apis.serviceDetail.editPlugin', 'Edit')}
                    </button>
                    <button className="text-red-600 hover:text-red-900 text-sm">
                      {t('apis.serviceDetail.removePlugin', 'Remove')}
                    </button>
                  </div>
                )}
              </div>
              
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  {t('apis.serviceDetail.configuration', 'Configuration')}
                </h4>
                <pre className="bg-gray-50 p-3 rounded text-sm overflow-auto">
                  {JSON.stringify(plugin.config, null, 2)}
                </pre>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ServiceDetail() {
  const { t } = useTranslation();
  const { serviceId } = useParams<{ serviceId: string }>();
  const { data: service, isLoading: serviceLoading, error: serviceError } = useApiService(serviceId!);
  const { data: routes, isLoading: routesLoading } = useApiRoutes(serviceId);
  const { data: plugins, isLoading: pluginsLoading } = useApiPlugins(serviceId);

  if (serviceLoading || routesLoading || pluginsLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded mb-6"></div>
          <div className="h-48 bg-gray-200 rounded mb-6"></div>
          <div className="h-48 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (serviceError || !service) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex">
          <div className="text-red-600 text-xl mr-3">⚠️</div>
          <div>
            <h3 className="text-lg font-medium text-red-800">
              {t('apis.serviceDetail.serviceNotFound', 'Service Not Found')}
            </h3>
            <p className="text-red-700 mt-1">
              {serviceError?.message || t('apis.serviceDetail.serviceNotFoundDescription', 'The requested service could not be found.')}
            </p>
            <Link
              to="/apis"
              className="mt-4 inline-block px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              {t('apis.serviceDetail.backToCatalog', 'Back to Catalog')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <nav className="flex mb-2" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <Link 
                  to="/apis" 
                  className="text-gray-500 hover:text-gray-700"
                >
                  {t('apis.serviceDetail.apiCatalog', 'API Catalog')}
                </Link>
              </li>
              <li>
                <span className="text-gray-400">/</span>
              </li>
              <li>
                <span className="text-gray-900 font-medium">{service.name}</span>
              </li>
            </ol>
          </nav>
          <h1 className="text-2xl font-semibold text-gray-900">
            {service.name}
          </h1>
          <p className="text-gray-600 mt-1">
            {t('apis.serviceDetail.subtitle', 'Service configuration and monitoring')}
          </p>
        </div>
        <div className="flex space-x-3">
          <Link
            to="/apis/test"
            className="px-4 py-2 text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {t('apis.serviceDetail.testService', 'Test Service')}
          </Link>
        </div>
      </div>

      {/* Service Information */}
      <ServiceInfo service={service} />

      {/* Routes */}
      <RoutesList routes={routes || []} />

      {/* Plugins */}
      <PluginsList plugins={plugins || []} />
    </div>
  );
}
